import { useCallback, useEffect, useMemo, useState } from "react";
import { getListRotationPeriods } from "../../../services/container";
import { getMonthsAgo } from "../../../components/formatted/FormattedDateAndTime";
import { displayDates } from "../../../shared/utils/utils";

const dedupePeriods = (periods) =>
  periods
    .filter(
      (period, index, self) =>
        self.findIndex((candidate) => candidate.name === period.name) === index,
    )
    .filter(
      (period, index, self) =>
        !self.some(
          (candidate, candidateIndex) =>
            candidateIndex !== index && candidate.start === period.start,
        ) || /\((?:[^()-]|\([^()]*\))*\)/.test(period.name),
    );
export const useRotationPeriods = ({
  site,
  superContainerId,
  i18n,
  monthsRange = 6,
  periodMaterialStart,
  periodMaterialStop,
}) => {
  const [periods, setPeriods] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const now = useMemo(() => new Date(), []);
  const fetchRotationTimeline = useCallback(async () => {
    if (!site?._id) {
      return [];
    }
    setIsLoading(true);
    setError(null);
    setPeriods([]);
    try {
      const startDate = periodMaterialStart
        ? new Date(periodMaterialStart).toISOString()
        : getMonthsAgo(now, monthsRange).toISOString();
      const stopDate = periodMaterialStop
        ? new Date(periodMaterialStop).toISOString()
        : now.toISOString();
      const response = await getListRotationPeriods(
        {
          periodMaterialStart: startDate,
          periodMaterialStop: stopDate,
          superContainerId,
          isCharacterization: true,
        },
        site._id,
      );
      if (response.status !== 200) {
        throw new Error("Unable to fetch rotation periods");
      }
      const formatted = (response.data?.periods ?? []).map((period, index) => {
        const client = period.client ? JSON.parse(period.client) : null;
        return {
          ...period,
          periodMaterialStartSubfilter: period.start,
          periodMaterialStopSubfilter: period.stop,
          name: displayDates(
            site,
            index,
            period.start,
            period.stop,
            client?.name ?? "-",
            client?.address ?? "-",
            i18n,
          ),
        };
      });
      const deduped = dedupePeriods(formatted);
      setPeriods(deduped);
      return deduped;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to fetch rotation periods", err);
      setError(err);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [
    i18n,
    monthsRange,
    now,
    site,
    superContainerId,
    periodMaterialStart,
    periodMaterialStop,
  ]);
  useEffect(() => {
    fetchRotationTimeline();
  }, [fetchRotationTimeline]);
  return {
    periods,
    isLoading,
    error,
    refetch: fetchRotationTimeline,
  };
};
